import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Loader2, Check, Beaker, Info, Droplets, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MAX_LOAD_EXTRA_PERCENT, getMaxLoadForMachine } from '../utils/loadLimits';

const ReceptionFormModal = ({ isOpen, onClose, onSave, item, fields, title, washingPrograms = [], machines = [], porcentajeHumedad = 0 }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [asyncOptions, setAsyncOptions] = useState({});
  const [loadingFields, setLoadingFields] = useState({});
  
  // Estados para búsqueda
  const [openEmpleadoPopover, setOpenEmpleadoPopover] = useState(false);
  const [empleadoSearchTerm, setEmpleadoSearchTerm] = useState('');
  const [openClientePopover, setOpenClientePopover] = useState(false);
  const [clienteSearchTerm, setClienteSearchTerm] = useState('');
  const [openTipoPrendaPopover, setOpenTipoPrendaPopover] = useState(false);
  const [tipoPrendaSearchTerm, setTipoPrendaSearchTerm] = useState('');
  const [openProgramaPopover, setOpenProgramaPopover] = useState(false);
  const [programaSearchTerm, setProgramaSearchTerm] = useState('');

  // Inicializa los datos del formulario
  useEffect(() => {
    if (!isOpen) return;
    
    const initialData = fields.reduce((acc, field) => {
      // Para campos que no son asyncSelect, simplemente copiar el valor
      if (field.type !== 'asyncSelect') {
        acc[field.id] = item ? item[field.id] : '';
      }
      return acc;
    }, {});
    
    // Establecer la fecha actual por defecto para createdAt si es una nueva recepción
    if (!item) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      initialData.createdAt = `${year}-${month}-${day}`;
    }
    
    setFormData(initialData);
    setErrors({});
  }, [isOpen, item, fields]);
  
  // Manejo de opciones asíncronas
  useEffect(() => {
    if (!isOpen) return;
    
    fields.forEach(field => {
      if (field.type === 'asyncSelect' && field.fetchOptions) {
        setLoadingFields(prev => ({ ...prev, [field.id]: true }));
        
        field.fetchOptions()
          .then(options => {
            setAsyncOptions(prev => ({ ...prev, [field.id]: options }));
            
            if (item && item[field.id]) {
              let selectedOption;
              
              // Busca la opción correspondiente basada en la estructura del objeto
              if (item[field.id] && item[field.id].id) {
                // Si es un objeto con id y nombre (como lavandería)
                selectedOption = options.find(opt => 
                  (opt.value.id && opt.value.id === item[field.id].id)
                );
              } else {
                // Si es un valor simple o un objeto con otra estructura
                selectedOption = options.find(opt => 
                  (opt.value === item[field.id]) ||
                  (opt.value.id === item[field.id])
                );
              }
              
              if (selectedOption) {
                setFormData(prev => ({ 
                  ...prev, 
                  [field.id]: selectedOption.value 
                }));
              }
            }
          })
          .catch(error => console.error(`Error cargando opciones para ${field.id}:`, error))
          .finally(() => setLoadingFields(prev => ({ ...prev, [field.id]: false })));
      }
    });
  }, [isOpen, fields, item]);

  const handleChange = (id, value) => {
    setFormData(prev => ({ ...prev, [id]: value }));
    if (errors[id]) {
      setErrors(prev => ({ ...prev, [id]: null }));
    }
    // Al cambiar de lavadora cambia el límite de carga, así que el error de
    // peso deja de ser válido y se recalcula al intentar guardar de nuevo.
    if (id === 'maquina' && errors.pesoKg) {
      setErrors(prev => ({ ...prev, pesoKg: null }));
    }
  };

  // Lavadora seleccionada y su límite de carga (capacidad + 30%).
  const selectedMachine = useMemo(() => {
    const maquinaId = typeof formData.maquina === 'object' ? formData.maquina?.id : formData.maquina;
    if (!maquinaId) return null;
    return machines.find(m => m._id === maquinaId) || null;
  }, [formData.maquina, machines]);

  const capacidadMaquina = Number(selectedMachine?.capacidad) || 0;
  const pesoMaximoPermitido = getMaxLoadForMachine(selectedMachine);
  const pesoIngresado = parseFloat(formData.pesoKg);
  const pesoValido = Number.isFinite(pesoIngresado) && pesoIngresado > 0;
  const excedeCapacidad = pesoMaximoPermitido > 0 && pesoValido && pesoIngresado > pesoMaximoPermitido;

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!pesoValido) {
      setErrors(prev => ({ ...prev, pesoKg: 'Ingresa un peso mayor a 0 kg.' }));
      return;
    }
    if (excedeCapacidad) {
      setErrors(prev => ({
        ...prev,
        pesoKg: `La lavadora ${selectedMachine?.nombre || ''} admite máximo ${pesoMaximoPermitido} kg.`,
      }));
      return;
    }

    onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  const getField = (id) => {
    const field = fields.find(f => f.id === id) || { options: [] };
    
    // Si es un campo asyncSelect, usamos las opciones cargadas
    if (field.type === 'asyncSelect') {
      return {
        ...field,
        options: asyncOptions[id] || [],
        loading: loadingFields[id] || false
      };
    }
    
    return field;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-background border-border rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] border flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          {/* Barra superior fija */}
          <div className="shrink-0 flex justify-between items-center gap-4 px-6 py-4 border-b bg-background">
            <div className="min-w-0">
              <h2 className="text-xl md:text-2xl font-semibold truncate">{title}</h2>
              {selectedMachine && (
                <p className="text-xs text-muted-foreground truncate">
                  {selectedMachine.nombre}
                  {capacidadMaquina > 0 && ` · capacidad ${capacidadMaquina} kg · máx. ${pesoMaximoPermitido} kg`}
                </p>
              )}
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={onClose} className="hover:bg-accent shrink-0">
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Cuerpo desplazable */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Fecha de Pesaje */}
          <div>
            <Label htmlFor="createdAt">Fecha de Pesaje</Label>
            <Input 
              id="createdAt" 
              type="date" 
              value={formData.createdAt ? new Date(formData.createdAt).toISOString().slice(0, 10) : ''} 
              onChange={(e) => handleChange('createdAt', e.target.value)} 
            />
          </div>
          {/* Empleado */}
          <div>
            <Label htmlFor="empleado">Lavador</Label>
            <div className="relative" id="empleado-input">
              <div className="relative flex items-center">
                <Input
                  type="text"
                  placeholder="Buscar lavador..."
                  value={empleadoSearchTerm || (
                    formData.empleado 
                      ? getField('empleado').options?.find(opt => (opt.value?.id || opt.value) === (formData.empleado?.id || formData.empleado))?.label || ''
                      : ''
                  )}
                  onChange={(e) => {
                    setEmpleadoSearchTerm(e.target.value);
                    setOpenEmpleadoPopover(true);
                  }}
                  onFocus={() => setOpenEmpleadoPopover(true)}
                  disabled={getField('empleado').loading}
                  className="w-full pr-8"
                />
                {formData.empleado && !empleadoSearchTerm && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChange('empleado', '');
                      setEmpleadoSearchTerm('');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              {openEmpleadoPopover && (
                <>
                  <div 
                    className="fixed inset-0 z-[9998]" 
                    onClick={() => {
                      setOpenEmpleadoPopover(false);
                      setEmpleadoSearchTerm('');
                    }}
                  />
                  <div 
                    className="fixed z-[9999] max-h-60 overflow-auto bg-background border rounded-md shadow-lg"
                    style={{
                      top: `${document.getElementById('empleado-input')?.getBoundingClientRect().bottom + 4}px`,
                      left: `${document.getElementById('empleado-input')?.getBoundingClientRect().left}px`,
                      width: `${document.getElementById('empleado-input')?.getBoundingClientRect().width}px`
                    }}
                  >
                    {getField('empleado').options
                      ?.filter(opt => {
                        const searchTerm = (empleadoSearchTerm || '').toLowerCase();
                        return opt.label.toLowerCase().includes(searchTerm);
                      })
                      .map(opt => {
                        const optValue = opt.value?.id || opt.value;
                        const currentValue = formData.empleado?.id || formData.empleado;
                        const isSelected = optValue === currentValue;
                        return (
                          <div
                            key={optValue}
                            className={cn(
                              "px-3 py-2 cursor-pointer hover:bg-accent flex items-center",
                              isSelected && "bg-accent/50"
                            )}
                            onClick={() => {
                              handleChange('empleado', optValue);
                              setOpenEmpleadoPopover(false);
                              setEmpleadoSearchTerm('');
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                isSelected ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <span className="text-sm">{opt.label}</span>
                          </div>
                        );
                      })}
                    {getField('empleado').options?.filter(opt => {
                      const searchTerm = (empleadoSearchTerm || '').toLowerCase();
                      return opt.label.toLowerCase().includes(searchTerm);
                    }).length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                        No se encontró el lavador
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          {/* Cliente */}
          <div>
            <Label htmlFor="cliente">Cliente</Label>
            <div className="relative" id="cliente-input">
              <div className="relative flex items-center">
                <Input
                  type="text"
                  placeholder="Buscar cliente..."
                  value={clienteSearchTerm || (
                    formData.cliente 
                      ? getField('cliente').options?.find(opt => (opt.value?.id || opt.value) === (formData.cliente?.id || formData.cliente))?.label || ''
                      : ''
                  )}
                  onChange={(e) => {
                    setClienteSearchTerm(e.target.value);
                    setOpenClientePopover(true);
                  }}
                  onFocus={() => setOpenClientePopover(true)}
                  disabled={getField('cliente').loading}
                  className="w-full pr-8"
                />
                {formData.cliente && !clienteSearchTerm && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChange('cliente', '');
                      setClienteSearchTerm('');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              {openClientePopover && (
                <>
                  <div 
                    className="fixed inset-0 z-[9998]" 
                    onClick={() => {
                      setOpenClientePopover(false);
                      setClienteSearchTerm('');
                    }}
                  />
                  <div 
                    className="fixed z-[9999] max-h-60 overflow-auto bg-background border rounded-md shadow-lg"
                    style={{
                      top: `${document.getElementById('cliente-input')?.getBoundingClientRect().bottom + 4}px`,
                      left: `${document.getElementById('cliente-input')?.getBoundingClientRect().left}px`,
                      width: `${document.getElementById('cliente-input')?.getBoundingClientRect().width}px`
                    }}
                  >
                    {getField('cliente').options
                      ?.filter(opt => {
                        const searchTerm = (clienteSearchTerm || '').toLowerCase();
                        return opt.label.toLowerCase().includes(searchTerm);
                      })
                      .map(opt => {
                        const optValue = opt.value?.id || opt.value;
                        const currentValue = formData.cliente?.id || formData.cliente;
                        const isSelected = optValue === currentValue;
                        return (
                          <div
                            key={optValue}
                            className={cn(
                              "px-3 py-2 cursor-pointer hover:bg-accent flex items-center",
                              isSelected && "bg-accent/50"
                            )}
                            onClick={() => {
                              handleChange('cliente', optValue);
                              setOpenClientePopover(false);
                              setClienteSearchTerm('');
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                isSelected ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <span className="text-sm">{opt.label}</span>
                          </div>
                        );
                      })}
                    {getField('cliente').options?.filter(opt => {
                      const searchTerm = (clienteSearchTerm || '').toLowerCase();
                      return opt.label.toLowerCase().includes(searchTerm);
                    }).length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                        No se encontró el cliente
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          {/* Tipo de Prenda */}
          <div>
            <Label htmlFor="tipoPrenda">Tipo de Prenda</Label>
            <div className="relative" id="tipoPrenda-input">
              <div className="relative flex items-center">
                <Input
                  type="text"
                  placeholder="Buscar tipo de prenda..."
                  value={tipoPrendaSearchTerm || (
                    formData.tipoPrenda 
                      ? getField('tipoPrenda').options?.find(opt => (opt.value?.id || opt.value) === (formData.tipoPrenda?.id || formData.tipoPrenda))?.label || ''
                      : ''
                  )}
                  onChange={(e) => {
                    setTipoPrendaSearchTerm(e.target.value);
                    setOpenTipoPrendaPopover(true);
                  }}
                  onFocus={() => setOpenTipoPrendaPopover(true)}
                  disabled={getField('tipoPrenda').loading}
                  className="w-full pr-8"
                />
                {formData.tipoPrenda && !tipoPrendaSearchTerm && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChange('tipoPrenda', '');
                      setTipoPrendaSearchTerm('');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              {openTipoPrendaPopover && (
                <>
                  <div 
                    className="fixed inset-0 z-[9998]" 
                    onClick={() => {
                      setOpenTipoPrendaPopover(false);
                      setTipoPrendaSearchTerm('');
                    }}
                  />
                  <div 
                    className="fixed z-[9999] max-h-60 overflow-auto bg-background border rounded-md shadow-lg"
                    style={{
                      top: `${document.getElementById('tipoPrenda-input')?.getBoundingClientRect().bottom + 4}px`,
                      left: `${document.getElementById('tipoPrenda-input')?.getBoundingClientRect().left}px`,
                      width: `${document.getElementById('tipoPrenda-input')?.getBoundingClientRect().width}px`
                    }}
                  >
                    {getField('tipoPrenda').options
                      ?.filter(opt => {
                        const searchTerm = (tipoPrendaSearchTerm || '').toLowerCase();
                        return opt.label.toLowerCase().includes(searchTerm);
                      })
                      .map(opt => {
                        const optValue = opt.value?.id || opt.value;
                        const currentValue = formData.tipoPrenda?.id || formData.tipoPrenda;
                        const isSelected = optValue === currentValue;
                        return (
                          <div
                            key={optValue}
                            className={cn(
                              "px-3 py-2 cursor-pointer hover:bg-accent flex items-center",
                              isSelected && "bg-accent/50"
                            )}
                            onClick={() => {
                              handleChange('tipoPrenda', optValue);
                              setOpenTipoPrendaPopover(false);
                              setTipoPrendaSearchTerm('');
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                isSelected ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <span className="text-sm">{opt.label}</span>
                          </div>
                        );
                      })}
                    {getField('tipoPrenda').options?.filter(opt => {
                      const searchTerm = (tipoPrendaSearchTerm || '').toLowerCase();
                      return opt.label.toLowerCase().includes(searchTerm);
                    }).length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                        No se encontró el tipo de prenda
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          {/* Maquina */}
          <div>
            <Label htmlFor="maquina">Lavadora</Label>
            <Select 
              onValueChange={(value) => handleChange('maquina', value)} 
              value={formData.maquina?.id || formData.maquina || ''}
              disabled={getField('maquina').loading}
            >
              <SelectTrigger>
                {getField('maquina').loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Cargando...</span>
                  </div>
                ) : (
                  <SelectValue placeholder="Seleccione una lavadora" />
                )}
              </SelectTrigger>
              <SelectContent>
                {getField('maquina').options.map(opt => {
                  const optId = opt.value?.id || opt.value;
                  const maquina = machines.find(m => m._id === optId);
                  const cap = Number(maquina?.capacidad) || 0;
                  return (
                    <SelectItem key={optId} value={optId}>
                      {opt.label}{cap > 0 ? ` · ${cap} kg` : ''}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Programa de Lavado */}
          <div>
            <Label htmlFor="programaLavado">Programa de Lavado</Label>
            <div className="relative" id="programa-input">
              <div className="relative flex items-center">
                <Input
                  type="text"
                  placeholder="Buscar programa..."
                  value={programaSearchTerm || (
                    formData.programaLavado 
                      ? getField('programaLavado').options?.find(opt => (opt.value?.id || opt.value) === (formData.programaLavado?.id || formData.programaLavado))?.label || ''
                      : ''
                  )}
                  onChange={(e) => {
                    setProgramaSearchTerm(e.target.value);
                    setOpenProgramaPopover(true);
                  }}
                  onFocus={() => setOpenProgramaPopover(true)}
                  disabled={getField('programaLavado').loading}
                  className="w-full pr-8"
                />
                {formData.programaLavado && !programaSearchTerm && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChange('programaLavado', '');
                      setProgramaSearchTerm('');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              {openProgramaPopover && (
                <>
                  <div 
                    className="fixed inset-0 z-[9998]" 
                    onClick={() => {
                      setOpenProgramaPopover(false);
                      setProgramaSearchTerm('');
                    }}
                  />
                  <div 
                    className="fixed z-[9999] max-h-60 overflow-auto bg-background border rounded-md shadow-lg"
                    style={{
                      top: `${document.getElementById('programa-input')?.getBoundingClientRect().bottom + 4}px`,
                      left: `${document.getElementById('programa-input')?.getBoundingClientRect().left}px`,
                      width: `${document.getElementById('programa-input')?.getBoundingClientRect().width}px`
                    }}
                  >
                    {getField('programaLavado').options
                      ?.filter(opt => {
                        const searchTerm = (programaSearchTerm || '').toLowerCase();
                        return opt.label.toLowerCase().includes(searchTerm);
                      })
                      .map(opt => {
                        const optValue = opt.value?.id || opt.value;
                        const currentValue = formData.programaLavado?.id || formData.programaLavado;
                        const isSelected = optValue === currentValue;
                        return (
                          <div
                            key={optValue}
                            className={cn(
                              "px-3 py-2 cursor-pointer hover:bg-accent flex items-center",
                              isSelected && "bg-accent/50"
                            )}
                            onClick={() => {
                              handleChange('programaLavado', optValue);
                              setOpenProgramaPopover(false);
                              setProgramaSearchTerm('');
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                isSelected ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <span className="text-sm">{opt.label}</span>
                          </div>
                        );
                      })}
                    {getField('programaLavado').options?.filter(opt => {
                      const searchTerm = (programaSearchTerm || '').toLowerCase();
                      return opt.label.toLowerCase().includes(searchTerm);
                    }).length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                        No se encontró el programa
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          {/* Peso (Kg) */}
          <div>
            <Label htmlFor="pesoKg">Peso (Kg)</Label>
            <Input
              id="pesoKg"
              type="number"
              min="0"
              step="0.01"
              value={formData.pesoKg || ''}
              onChange={(e) => handleChange('pesoKg', e.target.value)}
              className={cn(excedeCapacidad && 'border-destructive focus-visible:ring-destructive')}
            />
            {/* Solo se avisa cuando el peso supera el límite de carga de la lavadora
                (capacidad + 30%). Sin error no se muestra nada. */}
            {excedeCapacidad && (
              <div className="mt-1 flex items-start gap-1 text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded px-2 py-1">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                  <strong>{selectedMachine?.nombre}</strong> tiene capacidad de {capacidadMaquina} kg,
                  por lo que admite máximo <strong>{pesoMaximoPermitido} kg</strong>
                  {' '}({MAX_LOAD_EXTRA_PERCENT}% por encima de su capacidad).
                  Sobra {Number((pesoIngresado - pesoMaximoPermitido).toFixed(2))} kg.
                </span>
              </div>
            )}
            {errors.pesoKg && !excedeCapacidad && (
              <p className="mt-1 text-xs text-destructive">{errors.pesoKg}</p>
            )}
            {/*
              Preview en vivo del descuento por humedad configurado en el centro.
              Se oculta para dejar el campo de peso más limpio; el descuento se
              sigue aplicando al guardar (ver handleSave en DirtyClothesReceptionPage).
              {(() => {
                const pesoHumedos = parseFloat(formData.pesoKg);
                const pct = Number(porcentajeHumedad) || 0;
                if (!Number.isFinite(pesoHumedos) || pesoHumedos <= 0) return null;
                if (pct <= 0) {
                  return (
                    <p className="text-xs text-muted-foreground mt-1">
                      Sin descuento de humedad configurado en este centro.
                    </p>
                  );
                }
                const factor = 1 - Math.min(Math.max(pct, 0), 100) / 100;
                const pesoNeto = Number((pesoHumedos * factor).toFixed(2));
                const descuento = Number((pesoHumedos - pesoNeto).toFixed(2));
                return (
                  <div className="mt-1 flex items-center gap-1 text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded px-2 py-1">
                    <Droplets className="h-3.5 w-3.5" />
                    <span>
                      Humedad {pct}% → peso neto: <strong>{pesoNeto} kg</strong>
                      {' '}
                      <span className="text-sky-600/80">(descuento -{descuento} kg)</span>
                    </span>
                  </div>
                );
              })()}
            */}
          </div>

          {/* Observaciones */}
          <div className="md:col-span-2">
            <Label htmlFor="observaciones">Observaciones</Label>
            <Input id="observaciones" value={formData.observaciones || ''} onChange={(e) => handleChange('observaciones', e.target.value)} />
          </div>

          {/* Preview de productos a usar (calculado en vivo) */}
          <div className="md:col-span-2">
            {(() => {
              const programaId = typeof formData.programaLavado === 'object'
                ? formData.programaLavado?.id
                : formData.programaLavado;
              const programaObj = washingPrograms.find(p => p._id === programaId);

              const maquinaId = typeof formData.maquina === 'object'
                ? formData.maquina?.id
                : formData.maquina;
              const maquinaObj = machines.find(m => m._id === maquinaId);

              const peso = parseFloat(formData.pesoKg) || 0;
              const capacidad = Number(maquinaObj?.capacidad) || 0;
              const factorCarga = Number(programaObj?.factorCarga) || 0;
              const productos = Array.isArray(programaObj?.productos) ? programaObj.productos : [];
              const showPreview = !!programaObj && !!maquinaObj && peso > 0 && productos.length > 0;

              if (!showPreview) {
                return (
                  <div className="rounded-md border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    <span>
                      Selecciona lavadora, programa de lavado y un peso (kg) para ver el cálculo estimado de productos.
                    </span>
                  </div>
                );
              }

              const peso_total_factor_Carga = factorCarga > 0 ? (capacidad * 10) / factorCarga : 0;
              const totalMl = productos.reduce((sum, p) => sum + (Number(p.cantidadMililitros) || 0) * capacidad, 0);
              const usaCapacidadCompleta = capacidad > peso;

              return (
                <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <Beaker className="h-4 w-4" />
                      <span className="text-sm font-semibold">
                        Productos estimados para este lavado
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-emerald-700/80">
                      <span>
                        Cap. lavadora: <strong>{capacidad} kg</strong>
                      </span>
                      {factorCarga > 0 && (
                        <span>
                          Peso total factor carga: <strong>{peso_total_factor_Carga.toFixed(2)}</strong>
                        </span>
                      )}
                    </div>
                  </div>
                  {/*
                    {usaCapacidadCompleta && (
                    <div className="mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      La dosificación se calcula sobre la <strong>capacidad de la lavadora ({capacidad} kg)</strong>, no sobre el peso cargado ({peso} kg).
                    </div>
                    )} 
                   */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {productos.map((p, idx) => {
                      const base = Number(p.cantidadMililitros) || 0;
                      const calc = Number((base * capacidad).toFixed(2));
                      return (
                        <div key={idx} className="flex items-center justify-between rounded border border-emerald-100 bg-white px-2 py-1.5 text-xs">
                          <span className="truncate" title={p?.producto?.nombre || ''}>
                            {p?.producto?.nombre || 'Producto'}
                          </span>
                          <span className="font-mono text-emerald-700">
                            {base}ml × {capacidad}kg = <strong>{calc}ml</strong>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 text-right text-xs text-emerald-700">
                    Total estimado: <strong>{totalMl.toFixed(2)} ml</strong>
                  </div>
                </div>
              );
            })()}
          </div>

            </div>
          </div>

          {/* Barra inferior fija */}
          <div className="shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-4 border-t bg-background">
            <div className="text-xs">
              {excedeCapacidad && (
                <span className="inline-flex items-center gap-1 text-destructive font-medium">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Excede el máximo de {pesoMaximoPermitido} kg de esta lavadora.
                </span>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="submit" disabled={excedeCapacidad}>
                {item ? 'Guardar Cambios' : 'Crear'}
              </Button>
            </div>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default ReceptionFormModal;
